"use client";

import { useReadContract } from "wagmi";
import { GATEWAY_ABI } from "@/contract/abi/gateway";
import ContractAddress from "@/contract/addresses";
import { IS_GATEWAY_V2 } from "@/contract/gateway-version";

/**
 * The smallest gross BV the gateway will accept for a purchase, in wei.
 *
 * V2 rejects anything below this outright — `_validatePayment` is the FIRST
 * line of `_paymentProcess`, before the new-user branch, so it applies to
 * every purchase rather than only first ones:
 *
 *   if (grossBv < minimumPurchaseBv) revert GrossBvBelowMinimum(...)
 *
 * Read live rather than hardcoded because `setMinimumPurchaseBv` is `onlyDao`
 * — the value can change without a frontend deploy. Baking today's 1 BV in
 * would silently break checkout the day the DAO raises it, and the buyer
 * would meet a raw revert after signing instead of a message beforehand.
 *
 * Distinct from the placement threshold: clearing this minimum only means the
 * purchase is allowed. Entering the genealogy needs level 7 (`BVPlan[7]`,
 * currently 10 BV of EFFECTIVE bv, i.e. after the referral cut) — see
 * `Users._requiresPlacement`.
 */
export const useMinimumPurchaseBv = (): bigint => {
  const { data } = useReadContract({
    address: ContractAddress.GATEWAY,
    abi: GATEWAY_ABI,
    functionName: "minimumPurchaseBv",
    // Absent on V1, and absent on any V2 deployed before the purchase
    // safeguards landed — both revert rather than returning a value.
    query: { enabled: IS_GATEWAY_V2 },
  });

  // Fails OPEN (no minimum) when the read is unavailable. That matches the
  // behaviour of a gateway that has no such gate, so nothing that previously
  // succeeded starts being blocked by a failed read. The contract remains the
  // real enforcement; this hook only decides whether we can warn first.
  return typeof data === "bigint" ? data : 0n;
};
